import { Backdrop } from '@mui/material';
import UPButton from 'components/button';
import UPInput from 'components/input';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { deactivateKioskAction, setKioskErrorAction } from 'store/actions/kiosk-session-actions';
import { kioskErrorSelector, kioskLoadingSelector } from 'store/selectors/kiosk-session-selectors';
import {
  GENERAL__CANCEL_BUTTON,
  KIOSK__DEACTIVATE_BUTTON,
  KIOSK__DEACTIVATE_EMAIL_LABEL,
  KIOSK__DEACTIVATE_PASSWORD_LABEL,
  KIOSK__DEACTIVATE_SUBTITLE,
  KIOSK__DEACTIVATE_TITLE,
} from 'translations/constants';
import ModalProps from 'types/common/ModalProps';
import {
  KioskErrorMessage,
  KioskFieldContainer,
  KioskModalActions,
  KioskModalContainer,
  KioskModalStyled,
  KioskModalSubtitle,
  KioskModalTitle,
} from './styles';

interface IDeactivateForm {
  email: string;
  password: string;
}

const KioskDeactivateModal = ({ open, onClose }: ModalProps): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(kioskLoadingSelector);
  const error = useAppSelector(kioskErrorSelector);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IDeactivateForm>({ defaultValues: { email: '', password: '' } });

  const handleClose = (event: any) => {
    dispatch(setKioskErrorAction(null));
    onClose(event);
  };

  // On success the thunk redirects to sign-in; on INVALID_CREDENTIALS the shell stays locked.
  const onSubmit = (data: IDeactivateForm) => {
    dispatch(deactivateKioskAction({ email: data.email.trim(), password: data.password }));
  };

  return (
    // zIndex above KioskHeaderRow's 3 so the persistent reload/exit buttons dim under the backdrop too.
    <Backdrop open={open} sx={{ zIndex: 4 }}>
      <KioskModalStyled open={open} onClose={handleClose}>
        <KioskModalContainer>
          <KioskModalTitle>{t(KIOSK__DEACTIVATE_TITLE)}</KioskModalTitle>
          <KioskModalSubtitle>{t(KIOSK__DEACTIVATE_SUBTITLE)}</KioskModalSubtitle>

          <KioskFieldContainer>
            <Controller
              name="email"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <UPInput
                  value={field.value}
                  onChange={field.onChange}
                  label={t(KIOSK__DEACTIVATE_EMAIL_LABEL)}
                  placeholder=""
                  type="email"
                  required
                  error={!!errors.email}
                  id="kiosk-deactivate-email"
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <UPInput
                  value={field.value}
                  onChange={field.onChange}
                  label={t(KIOSK__DEACTIVATE_PASSWORD_LABEL)}
                  placeholder=""
                  type="password"
                  required
                  error={!!errors.password}
                  id="kiosk-deactivate-password"
                />
              )}
            />
          </KioskFieldContainer>

          {error && <KioskErrorMessage>{t(error)}</KioskErrorMessage>}

          <KioskModalActions>
            <UPButton text={t(GENERAL__CANCEL_BUTTON)} outlined onClick={handleClose} disabled={loading} />
            <UPButton
              text={t(KIOSK__DEACTIVATE_BUTTON)}
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
              id="kiosk-deactivate-submit"
            />
          </KioskModalActions>
        </KioskModalContainer>
      </KioskModalStyled>
    </Backdrop>
  );
};

export default KioskDeactivateModal;
